// src/services/dashboardDataService.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — shared, de-duplicated data loads for the dashboard widgets
//
// Several widgets (stat cards, charts, donut, map, AI snapshot) need the same lists.
// Without this, one live event made each of them download the full work-order list.
//   • concurrent calls share ONE in-flight request
//   • results are reused for `fresh` ms (default 3 s) so a burst of live refreshes
//     across widgets costs a single request
//   • { live: true } bypasses the workOrderService 5-minute cache, so changes made by
//     OTHER users (pushed over the WebSocket) are actually fetched
// ─────────────────────────────────────────────────────────────────────────────
import * as workOrderService from './workOrderService';
import * as activeSensorAlertService from './activeSensorAlertService';
import * as issueService from './issueService';

const FRESH_MS = 3000;
// --- live refreshes only reuse data fetched in the last second. Every live consumer debounces
// for >= 1.2 s, so anything that recent was fetched AFTER the event that triggered it.
const LIVE_FRESH_MS = 1000;
const entries = new Map(); // key → { inflight, data, at }

const shared = (key, loader, { live = false, fresh = live ? LIVE_FRESH_MS : FRESH_MS } = {}) => {
    const e = entries.get(key) || {};
    if (e.inflight) return e.inflight;
    if (e.data !== undefined && Date.now() - e.at < fresh) return Promise.resolve(e.data);
    const inflight = loader(live)
        .then((data) => { entries.set(key, { data, at: Date.now(), inflight: null }); return data; })
        .catch((err) => { entries.set(key, { ...entries.get(key), inflight: null }); throw err; });
    entries.set(key, { ...e, inflight });
    return inflight;
};

/** All work orders (same filters every widget uses, so the service cache is shared too). */
export const fetchAllWorkOrdersShared = (opts = {}) =>
    shared('workOrders', (live) => workOrderService.fetchWorkOrders({ limit: 10000, status: 'all', ...(live ? { forceRefresh: true } : {}) }), opts);

export const fetchActiveAlertsShared = (opts = {}) =>
    shared('alerts', () => activeSensorAlertService.fetchActiveAlerts(), opts);

export const fetchAllIssuesShared = (opts = {}) =>
    shared('issues', () => issueService.fetchAllIssues(), opts);

/** For tests / logout. */
export const resetDashboardDataCache = () => entries.clear();

const dashboardDataService = { fetchAllWorkOrdersShared, fetchActiveAlertsShared, fetchAllIssuesShared, resetDashboardDataCache };
export default dashboardDataService;
